import { generationSnapshotSchema } from "@ai-crm/shared/prompt-schema";
import { useState } from "react";
import {
  composeBusinessPrompt,
  type FunnelPromptFields,
  type FunnelDTO,
  type PromptBuilderInput,
  type PromptGenerationCandidate,
  type EscalationGenerationCandidate,
  type EscalationGenerationInput,
} from "@ai-crm/shared";
import {
  PromptBuilder,
  QUESTIONS,
  createEmptyPromptBuilderAnswers,
  type PromptBuilderState,
} from "./prompt-builder";
import {
  EscalationPromptEditor,
  GenerationRemarks,
  useGenerationCandidate,
} from "./escalation-prompt-editor";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { PaidFeatureGate, type PaidFeatureAccess } from "./paid-feature-gate";
const EXTRA_QUESTIONS = [
  { key: "role" as const, title: "Роль агента" },
  {
    key: "uncertaintyHandling" as const,
    title: "Что делать при нехватке данных?",
  },
  { key: "objectionHandling" as const, title: "Как работать с возражениями?" },
  { key: "responseExamples" as const, title: "Примеры ответов" },
];
export interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  fields: FunnelPromptFields;
  onFieldsChange: (fields: FunnelPromptFields) => void;
  generate: (
    input: PromptBuilderInput,
    signal?: AbortSignal,
  ) => Promise<PromptGenerationCandidate>;
  generateEscalation: (
    input: EscalationGenerationInput,
    signal?: AbortSignal,
  ) => Promise<EscalationGenerationCandidate>;
  agents?: readonly FunnelDTO[];
  loadAgent?: (
    id: string,
  ) => Promise<Pick<FunnelDTO, "systemPrompt" | "escalationPrompt">>;
  disabled?: boolean;
  escalationAccess?: PaidFeatureAccess;
}
export function PromptEditor({
  value,
  onChange,
  fields,
  onFieldsChange,
  generate,
  generateEscalation,
  agents = [],
  loadAgent,
  disabled = false,
  escalationAccess,
}: PromptEditorProps) {
  const [blockReplacementRevision, setBlockReplacementRevision] = useState(0);
  const answers = {
    ...(fields.promptForm ?? createEmptyPromptBuilderAnswers()),
    handoffConditions: fields.handoffConditions ?? "",
  };
  const [creationState, setCreationState] = useState<PromptBuilderState>({
    mode: "manual",
    answers,
  });
  const generation = useGenerationCandidate<{
    input: PromptBuilderInput;
    result: PromptGenerationCandidate;
  }>(JSON.stringify([answers, value, fields.escalationPrompt]));
  const snapshotValid =
    generation.candidate === null ||
    generationSnapshotSchema.safeParse({
      schemaVersion: 1,
      input: generation.candidate.input,
      result: generation.candidate.result,
    }).success;
  const total = Object.values(answers).reduce(
    (n, value) => n + (value?.length ?? 0),
    0,
  );
  function updateAnswer(key: keyof PromptBuilderInput, text: string) {
    if (key === "handoffConditions" && escalationAccess?.allowed === false) return;
    onFieldsChange({
      ...fields,
      promptForm: { ...answers, [key]: text },
      handoffConditions:
        key === "handoffConditions" ? text : answers.handoffConditions,
    });
  }
  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Редактор инструкции"
        className="flex gap-2"
      >
        {(["FORM", "MANUAL"] as const).map((mode) => (
          <Button
            key={mode}
            type="button"
            role="tab"
            aria-selected={fields.promptMode === mode}
            disabled={disabled}
            onClick={() => onFieldsChange({ ...fields, promptMode: mode })}
          >
            {mode === "FORM" ? "Форма" : "Промпт"}
          </Button>
        ))}
      </div>
      <div hidden={fields.promptMode !== "FORM"} className="space-y-3">
        {[...QUESTIONS, ...EXTRA_QUESTIONS].map((question) => (
          <PaidFeatureGate key={question.key} allowed={question.key !== "handoffConditions" || escalationAccess?.allowed !== false}
            title="Условия передачи менеджеру" preview={<div className="min-h-52 rounded border bg-muted p-4">Условия передачи менеджеру</div>} upgradeAction={escalationAccess?.upgradeAction}>
          <div>
            <Label htmlFor={`editor-${question.key}`}>{question.title}</Label>
            <Textarea
              id={`editor-${question.key}`}
              rows={2}
              maxLength={4000}
              value={answers[question.key] ?? ""}
              onChange={(event) =>
                updateAnswer(question.key, event.target.value)
              }
              disabled={disabled}
            />
          </div>
          </PaidFeatureGate>
        ))}
        {total > 16000 && (
          <p role="alert">Анкета не может быть длиннее 16000 символов.</p>
        )}
        <Button
          type="button"
          disabled={disabled || total > 16000}
          onClick={() =>
            generation.generate(async (signal) => ({
              input: structuredClone(answers),
              result: await generate(answers, signal),
            }))
          }
        >
          {generation.pending ? "Составить заново" : "Составить промпт"}
        </Button>
      </div>
      <div hidden={fields.promptMode !== "MANUAL"}>
        <PromptBuilder
          generatePrompt={generate}
          value={value}
          onChange={onChange}
          state={creationState}
          onStateChange={(next) => {
            if (next.mode === "assistant")
              onFieldsChange({ ...fields, promptMode: "FORM" });
            else setCreationState(next);
          }}
          agents={agents}
          copyContextSignature={JSON.stringify(fields)}
          {...(escalationAccess ? { escalationAccess } : {})}
          {...(loadAgent ? { loadAgent } : {})}
          onCopyBlock={(escalationPrompt) => {
            if (escalationAccess?.allowed === false) return;
            setBlockReplacementRevision((revision) => revision + 1);
            onFieldsChange({
              ...fields,
              promptMode: "MANUAL",
              escalationPrompt,
            });
          }}
          disabled={disabled}
        />
      </div>
      {generation.error && <p role="alert">{generation.error}</p>}
      {generation.candidate && (
        <section
          className="space-y-2 rounded border p-3"
          aria-label="Результат генерации"
        >
          <pre className="whitespace-pre-wrap">
            {composeBusinessPrompt({
              systemPrompt: generation.candidate.result.prompt,
              escalationPrompt: escalationAccess?.allowed === false ? null : generation.candidate.result.escalationPrompt,
            })}
          </pre>
          <GenerationRemarks {...generation.candidate.result} />
          {generation.stale && (
            <p role="alert">
              Результат устарел. Форма или ручной текст изменились —
              сгенерируйте заново.
            </p>
          )}
          {!snapshotValid && (
            <p role="alert">
              Результат слишком велик для сохранения. Сократите анкету и
              сгенерируйте заново.
            </p>
          )}
          <Button
            type="button"
            disabled={
              disabled ||
              generation.stale ||
              generation.pending ||
              !snapshotValid
            }
            onClick={() => {
              const candidate = generation.candidate;
              if (!candidate || generation.stale || !snapshotValid) return;
              setBlockReplacementRevision((revision) => revision + 1);
              onChange(candidate.result.prompt);
              onFieldsChange({
                ...fields,
                promptMode: "MANUAL",
                promptForm: answers,
                handoffConditions: escalationAccess?.allowed === false ? fields.handoffConditions : answers.handoffConditions,
                generatedPrompt: candidate.result.prompt,
                escalationPrompt: escalationAccess?.allowed === false ? fields.escalationPrompt : candidate.result.escalationPrompt,
                generationSnapshot: {
                  schemaVersion: 1,
                  input: candidate.input,
                  result: candidate.result,
                },
              });
              generation.clear();
              setCreationState({ ...creationState, mode: "manual" });
            }}
          >
            Использовать промпт
          </Button>
        </section>
      )}
      <PaidFeatureGate allowed={escalationAccess?.allowed !== false} title="Инструкция передачи менеджеру" preview={<div className="min-h-52 rounded border bg-muted p-4">Условия и инструкция передачи менеджеру</div>} upgradeAction={escalationAccess?.upgradeAction}>
      <div hidden={fields.promptMode === "FORM"}>
        <Label htmlFor="editor-handoff-manual">
          Условия передачи менеджеру
        </Label>
        <Textarea
          id="editor-handoff-manual"
          value={fields.handoffConditions ?? ""}
          maxLength={4000}
          onChange={(event) =>
            updateAnswer("handoffConditions", event.target.value)
          }
          disabled={disabled}
        />
      </div>
      <EscalationPromptEditor
        key={blockReplacementRevision}
        systemPrompt={value}
        handoffConditions={fields.handoffConditions ?? ""}
        value={fields.escalationPrompt}
        onChange={(escalationPrompt) =>
          onFieldsChange({ ...fields, escalationPrompt })
        }
        generate={generateEscalation}
        disabled={disabled}
        generatedConditions={
          fields.escalationPrompt ===
          fields.generationSnapshot?.result.escalationPrompt
            ? fields.generationSnapshot.input.handoffConditions
            : null
        }
      />
      </PaidFeatureGate>
      <details>
        <summary>Предпросмотр действующей инструкции после сохранения</summary>
        <pre className="whitespace-pre-wrap">
          {composeBusinessPrompt({
            systemPrompt: value,
            escalationPrompt: escalationAccess?.allowed === false ? null : fields.escalationPrompt,
          })}
        </pre>
      </details>
      {fields.generatedPrompt !== null && (
        <details>
          <summary>Исходная генерация</summary>
          <pre className="whitespace-pre-wrap">{fields.generatedPrompt}</pre>
        </details>
      )}
    </div>
  );
}
